import { createContext, useContext, useMemo } from 'react';

import { FloatingActionButtons } from '@/components/molecules/floating-action-buttons';
import { useEmployeesCtx } from '@/components/providers/employee-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useEditableStateUpdates } from '@/hooks/use-editable-state';

type ApiEmployee = ApiSchemas['EmployeeRead'];

function transform(employee: ApiEmployee) {
  const { employee_regions, employee_skills, ...rest } = employee;
  return {
    ...rest,
    start_location: employee.start_location as ApiSchemas['LocationUpsert'] | null | undefined,
    end_location: employee.end_location as ApiSchemas['LocationUpsert'] | null | undefined,
    regions_ids: employee_regions.map((region) => region.office_region_id) || [],
    skills_ids: employee_skills.map((skill) => skill.skill_id) || [],
  };
}

type Employee = ReturnType<typeof transform>;

interface Context {
  name: string;
  employee: Employee;
  update: (v: Partial<Employee>) => void;
}

const EmployeeEditingContext = createContext<Context | null>(null);

export function useEmployeeEditingCtx() {
  const ctx = useContext(EmployeeEditingContext);
  if (!ctx) {
    throw new Error('useEmployeeEditingCtx must be used within a EmployeeEditingProvider');
  }
  return ctx;
}

export function EmployeeEditingProvider({ id, children }: { id: string; children: React.ReactNode }) {
  const { employeesById } = useEmployeesCtx();
  const employee = useMemo(() => employeesById.get(id), [employeesById, id]);
  if (!employee) {
    return null;
  }
  return <Provider employee={employee}>{children}</Provider>;
}

function Provider({ employee, children }: { employee: ApiEmployee; children: React.ReactNode }) {
  const { api } = useApiV1Ctx();
  const state = useEditableStateUpdates(employee, transform);

  const name = useMemo(() => {
    return `${employee.first_name} ${employee.last_name}`.trim();
  }, [employee]);

  const mutation = api.employees.updateEmployeesEmployeeIdPut.useMutation(
    {
      path: { employee_id: employee.id },
    },
    {
      onSuccess: (data) => {
        state.reset(data);
        api.employees.getAllEmployeesGet.invalidateQueries();
      },
    }
  );

  const onSave = () => {
    if (!state.isDirty) return;
    mutation.mutate(state.updates);
  };

  return (
    <EmployeeEditingContext.Provider value={{ name, employee: state.value, update: state.setUpdates }}>
      {children}
      {state.isDirty && (
        <FloatingActionButtons onSave={onSave} onDiscard={() => state.reset(employee)} disabled={mutation.isPending} />
      )}
    </EmployeeEditingContext.Provider>
  );
}
